import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import FlowEngineSection from "./FlowEngineSection";
import { flowCaseKey, summarizeCaseMassSelection } from "../../app/helpers/flowCaseMassHelper";
import { getModuleDisplayLabel } from "../../app/helpers/flowEngineConfigHelper";
import "./flowListSelectionBar.css";

const FlowCaseMassToolbar = ({
  records = [],
  selectedRecords = [],
  onSelectionChange,
  onAfterAction,
  showFlow = true,
  roleId = null,
  poolSurface = "",
  roleKey = "",
  canSelectRecord = null,
  subjectKind = ""
}) => {
  const { t } = useTranslation();
  const summary = summarizeCaseMassSelection(selectedRecords, { roleKey: roleKey || poolSurface });
  const selectableRecords = typeof canSelectRecord === "function"
    ? records.filter((row) => canSelectRecord(row))
    : records;
  const selectedKeys = new Set(selectedRecords.map(flowCaseKey));
  const selectedOnPage = selectableRecords.filter((row) => selectedKeys.has(flowCaseKey(row))).length;
  const allPageSelected = selectableRecords.length > 0
    && selectableRecords.every((row) => selectedKeys.has(flowCaseKey(row)));

  const handlePageSelection = () => {
    if (allPageSelected) {
      const pageKeys = new Set(selectableRecords.map(flowCaseKey));
      onSelectionChange?.(selectedRecords.filter((item) => !pageKeys.has(flowCaseKey(item))));
      return;
    }
    const merged = [...selectedRecords];
    selectableRecords.forEach((row) => {
      if (!selectedKeys.has(flowCaseKey(row))) {
        merged.push(row);
      }
    });
    onSelectionChange?.(merged);
  };

  return (
    <div className="mb-3">
      <div className="flow-list-selection-bar">
        <div className="flow-list-selection-bar__left">
          <span className="flow-list-selection-bar__count">
            <i className="fa fa-check-square" aria-hidden="true" />
            <strong>{t("general.flow-engine.selected-label", "Seleccionados:")}</strong>
            {" "}
            {selectedRecords.length}
            {" / "}
            {records.length}
            {selectedOnPage !== selectedRecords.length ? (
              <span className="text-muted ms-1">
                ({t("flow.mass.on-page", "{{count}} en esta página", { count: selectedOnPage })})
              </span>
            ) : null}
          </span>
          {selectableRecords.length > 0 && (
            <button
              type="button"
              className="flow-list-selection-bar__action"
              onClick={handlePageSelection}
            >
              {allPageSelected
                ? t("general.flow-engine.deselect-page", "Deseleccionar")
                : t("general.flow-engine.select-page", "Seleccionar página")}
            </button>
          )}
        </div>
      </div>
      {selectedRecords.length > 0 && !summary.canFlow && (
        <div className="alert alert-warning py-2">
          {summary.anyPublished
            ? t(
              "flow.mass.published",
              "Hay registros publicados en la selección; solo Coordinador o Enlace pueden operarlos."
            )
            : t(
              "flow.mass.need-same-module-state",
              "La operación masiva de flujo requiere registros del mismo módulo y el mismo estado."
            )}
        </div>
      )}
      {showFlow && summary.canFlow && (
        <FlowEngineSection
          record={selectedRecords[0]}
          moduleProcess={summary.process}
          moduleName={getModuleDisplayLabel(summary.process, t)}
          selectedRecords={selectedRecords}
          onAfterAction={onAfterAction}
          roleId={roleId}
          poolSurface={poolSurface}
          subjectKind={subjectKind}
        />
      )}
    </div>
  );
};

FlowCaseMassToolbar.propTypes = {
  records: PropTypes.array,
  selectedRecords: PropTypes.array,
  onSelectionChange: PropTypes.func,
  onAfterAction: PropTypes.func,
  showFlow: PropTypes.bool,
  roleId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  poolSurface: PropTypes.string,
  roleKey: PropTypes.string,
  canSelectRecord: PropTypes.func,
  subjectKind: PropTypes.string
};

export default FlowCaseMassToolbar;
